import React, { useState, useEffect, useRef } from 'react';
import qs from 'qs';
import { useRange } from 'react-instantsearch';
import DoubleRangeSlider from '../../../components/double-range-slider/DoubleRangeSlider';
import SaleItems from './SaleItems';
import Button from '@jbhi-fi/lanyard-ui/components/Button';
import { updateSearchRefFromUrl } from './utils';
import { pushToDataLayer } from '@jbhi-fi/utils/src/ga4Tracking/datalayer';
import { getSession, SessionStorageKeys } from '../../../utils/sessionStorage';
import { postAlgoliaFilterClick, postAlgoliaFilterView } from '../../../utils/algolia/search-insights';

const DEBOUNCE_TIME = 400;

interface PriceRangeSliderProps {
  setCurrentRefinement: any;
  nbHits: number;
  showDollar: boolean;
  showDropdown: boolean;
  trackingInfo: any;
  attribute: string;
  defaultRefinement: any;
}

interface PriceRangeDropdownProps {
  attribute: string;
  facet: string;
  nbHits: number;
  saleItems: string | boolean;
  defaultRefinement: any;
  nbSaleItems: number;
  setDisableScroll?: any;
  trackingInfo: any;
  onDropdownToggle?: (isOpen: boolean) => void;
}

const PriceRangeSlider = ({
  setCurrentRefinement,
  nbHits,
  showDollar,
  showDropdown,
  trackingInfo,
  attribute,
}: PriceRangeSliderProps) => {
  const { range, start, refine } = useRange({ attribute });
  const { min, max }: { min: any; max: any } = range;
  const startMin: any = Number.isFinite(start[0]) ? start[0] : min;
  const startMax: any = Number.isFinite(start[1]) ? start[1] : max;
  const currentRefinement = {
    min: Math.max(min, startMin),
    max: Math.min(max, startMax),
  };
  const [debouncedState, setDebouncedState] = useState(null);
  const searchState = qs.parse(window.location.search.slice(1)) as any;
  const searchRef = useRef(searchState);
  const [prevMin, setPrevMin] = useState(min);
  const [prevMax, setPrevMax] = useState(max);
  const [priceMethod, setPriceMethod] = useState('text');

  const handleSetPriceMethod = method => {
    setPriceMethod(method);
  };

  useEffect(() => {
    setCurrentRefinement({
      ...currentRefinement,
      isRefined: currentRefinement && (currentRefinement.min !== min || currentRefinement.max !== max),
    });
  }, [currentRefinement.min, currentRefinement.max]);

  useEffect(() => {
    postAlgoliaFilterClick('price', `>=${currentRefinement.min}`);
  }, [currentRefinement.min]);

  useEffect(() => {
    postAlgoliaFilterClick('price', `<=${currentRefinement.max}`);
  }, [currentRefinement.max]);

  useEffect(() => {
    if (min && max && currentRefinement.min && currentRefinement.max) {
      if (currentRefinement.max > max || currentRefinement.max < min) {
        refine([currentRefinement.min, max]);
      } else if (currentRefinement.min > max || currentRefinement.min < min) {
        refine([min, currentRefinement.max]);
      }
    }
  }, [min, max, currentRefinement.min, currentRefinement.max]);

  const onRefine = ({ currentMin, currentMax }) => {
    clearTimeout(debouncedState);

    setDebouncedState(
      setTimeout(() => {
        const minChanged = min !== currentMin;
        const maxChanged = max !== currentMax;
        refine([currentMin, currentMax]);

        if (minChanged && prevMin !== currentMin) {
          pushToDataLayer({
            event: 'select_filter',
            filter_group: 'Price',
            filter_name: 'min price',
            filter_type: priceMethod,
            filter_value: currentMin,
            search_type: getSession(SessionStorageKeys.SearchMethod) || 'site search',
            search_term: searchState?.query || '',
          });
          setPrevMin(currentMin);
        }
        if (maxChanged && prevMax !== currentMax) {
          pushToDataLayer({
            event: 'select_filter',
            filter_group: 'Price',
            filter_name: 'max price',
            filter_type: priceMethod,
            filter_value: currentMax,
            search_type: getSession(SessionStorageKeys.SearchMethod) || 'site search',
            search_term: searchState?.query || '',
          });
          setPrevMax(currentMax);
        }

        const filters = Object.keys(searchState)
          .filter(key => !['query', 'page', 'hitsPerPage', 'sortBy'].includes(key))
          .reduce((obj, key) => {
            if (key.toLowerCase() === 'price') {
              obj[key.toLowerCase()] = { type: 'range', value: searchState[key] };
            } else {
              obj[key.toLowerCase()] = Array.isArray(searchState[key])
                ? searchState[key].map((i: any) => ({ type: 'checkbox', value: i }))
                : { type: 'checkbox', value: searchState[key] };
            }
            return obj;
          }, {});

        updateSearchRefFromUrl(searchRef);

        window.dataLayer.push({
          event: 'custom.filter.set',
          group: 'site search',
          category: 'search',
          label: min === currentMin && max === currentMax ? 'off' : 'on',
          name: 'price',
          value: {
            min: currentMin,
            max: currentMax,
          },
          info: {
            element: 'slider',
            position: trackingInfo.position,
            sortyBy: searchRef.current.sortBy || trackingInfo.defaults.sortBy,
            hitsPerPage: Number(searchRef.current.hitsPerPage) || trackingInfo.defaults.hitsPerPage,
          },
          newFilters: {
            ...filters,
            price: {
              type: 'range',
              value: {
                min: currentMin,
                max: currentMax,
              },
            },
          },
          oldFilters: filters,
        });
      }, DEBOUNCE_TIME) as any
    );
  };

  /**
   * Render function
   */
  return (
    <DoubleRangeSlider
      onSetPriceMethod={handleSetPriceMethod}
      showDollar={showDollar}
      nbHits={nbHits}
      min={min}
      max={max}
      currentMin={currentRefinement.min}
      currentMax={currentRefinement.max || 0}
      onRefine={onRefine}
      showDropdown={showDropdown}
    />
  );
};

const PriceRangeDropdown = ({
  attribute,
  facet,
  nbHits,
  saleItems,
  defaultRefinement,
  nbSaleItems,
  setDisableScroll,
  trackingInfo,
  onDropdownToggle,
}: PriceRangeDropdownProps) => {
  const [showDropdown, setShowDropdown] = useState(false);
  const RefineButton = useRef(null);
  const RefineDropdown = useRef(null);
  const [currentRefinement, setCurrentRefinement] = useState({ isRefined: false, min: 0, max: 0 });
  const [currentSaleItems, setCurrentSaleItems] = useState(saleItems);
  const searchState = qs.parse(window.location.search.slice(1));

  // Remove the event listener on unmount
  useEffect(() => {
    setCurrentSaleItems(saleItems);

    return () => {
      document.body.removeEventListener('click', externalClick.bind(this));
    };
  }, []);

  /**
   * Function to handle external clicks (clicks not within the price range dropdown or button)
   */
  const externalClick = (event: { target: any }) => {
    const PriceRangeButton = RefineButton.current;
    const PriceRangeDropdown = RefineDropdown.current;

    // Close price range dropdown if clicked outside of target window (desktop)
    if (
      !event ||
      (PriceRangeDropdown &&
        !PriceRangeDropdown.contains(event.target) &&
        PriceRangeButton &&
        !PriceRangeButton.contains(event.target))
    ) {
      setShowDropdown(false);
      setDisableScroll(false);
    }
  };

  /**
   * Function to handle dropdown toggle (clicking button)
   */
  const toggleDropdown = (event: { preventDefault: () => void }) => {
    onDropdownToggle?.(!showDropdown);
    pushToDataLayer({
      event: 'open_filter_group',
      filter_group: 'price',
      search_type: getSession(SessionStorageKeys.SearchMethod) || 'site search',
      search_term: searchState?.query || '',
    });
    postAlgoliaFilterView('price');
    event.preventDefault();

    if (!showDropdown === true) {
      // Add event listener for dismissing the dropdown when a user clicks away
      setShowDropdown(true);
      document.body.addEventListener('click', externalClick.bind(this));
      setDisableScroll(true);
    } else {
      // Remove event listener when dropdown hides
      document.body.removeEventListener('click', externalClick.bind(this));
      setShowDropdown(false);
      setDisableScroll(false);
    }
  };

  /**
   * Render function
   */
  return (
    <div className="search-results-facet">
      <button
        type="button"
        className={`search-results-facet-button${showDropdown ? ' active' : ''}${
          currentRefinement.isRefined || currentSaleItems ? ' refined' : ''
        }`}
        ref={RefineButton}
        onClick={toggleDropdown.bind(this)}
      >
        {facet}
        {currentRefinement.isRefined &&
          currentRefinement?.min &&
          currentRefinement?.max &&
          ` (${facet === 'Price' ? '$' : ''}${currentRefinement.min} - ${facet === 'Price' ? '$' : ''}${
            currentRefinement.max
          })`}
      </button>
      <div
        ref={RefineDropdown}
        className={`search-results-facet-dropdown scoped-form-styles ${showDropdown ? 'active' : ''}`}
      >
        <div className="resultNum">
          {nbHits} result{nbHits !== 1 ? 's' : ''}
        </div>
        <PriceRangeSlider
          attribute={attribute}
          defaultRefinement={defaultRefinement}
          showDollar={facet === 'Price'}
          nbHits={nbHits}
          setCurrentRefinement={setCurrentRefinement}
          showDropdown={showDropdown}
          trackingInfo={trackingInfo}
        />
        {facet === 'Price' && (
          <SaleItems
            attribute="onPromotion"
            label="Sale items"
            defaultRefinement={saleItems}
            setCurrentSaleItems={setCurrentSaleItems}
            nbSaleItems={nbSaleItems}
            trackingInfo={trackingInfo}
          />
        )}
        <Button classes="ais-ShowResults" type="primary" onClick={toggleDropdown.bind(this)} label="Show results" />
      </div>
    </div>
  );
};

export default PriceRangeDropdown;
